/**
 * The shared sentiment scale — one definition, used by every widget that plots
 * Positive → Negative so the same category is never a different color in two
 * places on the same dashboard.
 *
 * Consumers: SentimentSplitWidget (100% stacked bars), DonutPieWidget (donut_pie
 * sentiment payloads).
 *
 * ── Why green→red ──────────────────────────────────────────────────────────
 * Explicit product request: green = positive, red = negative. This is a
 * conscious override of the more colorblind-accessible blue→red diverging
 * scale this file used previously (deutan ΔE 16.2 vs green→red's measured
 * 7.2 — the classic red/green collapse for colorblind readers). If secondary
 * encoding (labels/legend, already present on every consumer) isn't enough
 * to compensate, revisit before shipping further.
 *
 * "Mixed" stays amber rather than a second grey: a diverging scale is light
 * at its midpoint, so Neutral and Mixed would both sit near that light
 * middle and be hard to tell apart at a glance. Amber is also the more
 * truthful encoding — "Mixed" means both signals are present, which isn't a
 * point on the positive→negative line at all.
 */

export type SentimentLevelKey =
  | "positive"
  | "mostly_positive"
  | "neutral"
  | "mixed"
  | "mostly_negative"
  | "negative";

export type SentimentLevel = {
  key: SentimentLevelKey;
  label: string;
  color: string;
  /** Matches a payload category name against this level. */
  test: (name: string) => boolean;
};

// Ordered Positive → Negative. The order is the reading order and drives both
// segment order and the color ramp, so don't reorder without reason.
//
// Every `test` carries an explicit `!mostly` guard where needed, so
// "Mostly Positive" can never be swallowed by the "Positive" rule regardless of
// how these are declared or searched.
export const SENTIMENT_LEVELS: SentimentLevel[] = [
  {
    key: "positive",
    label: "Positive",
    color: "#15803D",
    test: (n) => /positive/i.test(n) && !/mostly/i.test(n),
  },
  {
    key: "mostly_positive",
    label: "Mostly positive",
    color: "#4ADE80",
    test: (n) => /mostly\s*positive/i.test(n),
  },
  {
    key: "neutral",
    label: "Neutral",
    color: "#64748B",
    test: (n) => /neutral/i.test(n),
  },
  {
    key: "mixed",
    label: "Mixed",
    color: "#F59E0B",
    test: (n) => /mixed/i.test(n),
  },
  {
    key: "mostly_negative",
    label: "Mostly negative",
    color: "#F87171",
    test: (n) => /mostly\s*negative/i.test(n),
  },
  {
    key: "negative",
    label: "Negative",
    color: "#DC2626",
    test: (n) => /negative/i.test(n) && !/mostly/i.test(n),
  },
];

/** The level a category name belongs to, or undefined if it isn't sentiment. */
export const findSentimentLevel = (
  name: string,
): SentimentLevel | undefined =>
  SENTIMENT_LEVELS.find((level) => level.test(name));

/** Scale position, for sorting categories into reading order. */
export const sentimentRank = (key: string): number =>
  SENTIMENT_LEVELS.findIndex((level) => level.key === key);
